import * as L from "leaflet";
import { BehaviorSubject } from "rxjs";
import { filter } from "rxjs/operators";
import { QuadTreeStorage } from "./storage/QuadTreeStorage";
import { ListStorage } from "./storage/ListStorage";
export class CanvasLayer extends L.Layer {
    constructor(options) {
        super(options);
        this.options = {
            zIndex: 400,
            padding: 0,
        };
        this.moving = false;
        this._zoom = 0;
        this._center = new L.LatLng(0, 0);
        this.mouse_range = 3;
        this.setOptions(options);
        this.canvas = L.DomUtil.create("canvas", "leaflet-layer");
    }
    static create(options) {
        let obj = new this();
        obj.setOptions(options);
        return obj;
    }
    setOptions(options) {
        if (options) {
            this.options = Object.assign(this.options, options);
        }
    }
    getMap() {
        if (!this.map) {
            throw new Error("No map defined for layer");
        }
        return this.map;
    }
    getCanvas() {
        if (!this.canvas) {
            throw new Error("No canvas defined for layer");
        }
        return this.canvas;
    }
    getContext() {
        const canvas = this.getCanvas();
        const ctx = canvas.getContext("2d");
        if (!ctx) {
            throw new Error("Unable to aquire context, please check map and canvas");
        }
        return ctx;
    }
    onAdd(map) {
        this.map = map;
        const animated = map.options.zoomAnimation && L.Browser.any3d;
        L.DomUtil.addClass(this.canvas, "leaflet-zoom-" + (animated ? "animated" : "hide"));
        map.getPanes().overlayPane.appendChild(this.canvas);
        this.canvas.style.zIndex = `${this.options.zIndex}`;
        map.on("viewreset", this.canvasReset, this);
        map.on("movestart", this.movestart, this);
        map.on("moveend", this.moveend, this);
        map.on("zoomend", this.canvasReset, this);
        map.on("zoom", this.onZoom, this);
        map.on("resize", this.canvasReset, this);
        map.on("click", this.handleClick, this);
        if (animated) {
            map.on("zoomanim", this.animateZoom, this);
        }
        this.canvasReset();
        return this;
    }
    onRemove(map) {
        const animated = map.options.zoomAnimation && L.Browser.any3d;
        map.getPanes().overlayPane.removeChild(this.canvas);
        map.off("viewreset", this.canvasReset, this);
        map.off("movestart", this.movestart, this);
        map.off("moveend", this.moveend, this);
        map.off("zoom", this.onZoom, this);
        map.off("resize", this.canvasReset, this);
        map.off("click", this.handleClick, this);
        if (animated) {
            map.off("zoomanim", this.animateZoom, this);
        }
        return this;
    }
    movestart() {
        this.moving = true;
    }
    moveend() {
        this.moving = false;
        this.canvasReset();
    }
    onZoom() {
        let map = this.getMap();
        this._updateTransform(map.getCenter(), map.getZoom());
    }
    animateZoom(e) {
        this._updateTransform(e.center, e.zoom);
    }
    _calculate_offset(center, zoom) {
        let map = this.getMap();
        let scale = map.getZoomScale(zoom, this._zoom);
        let containerOffset = map.layerPointToContainerPoint([0, 0]), viewHalf = map.getSize().multiplyBy(0.5 + this.options.padding), currentCenterPoint = map.project(this._center, zoom), destCenterPoint = map.project(center, zoom), centerOffset = destCenterPoint.subtract(currentCenterPoint);
        return viewHalf
            .multiplyBy(1 - scale)
            .subtract(centerOffset)
            .subtract(containerOffset);
    }
    _updateTransform(center, zoom) {
        let map = this.getMap();
        let scale = map.getZoomScale(zoom, this._zoom);
        let topLeftOffset = this._calculate_offset(center, zoom);
        if (L.Browser.any3d) {
            L.DomUtil.setTransform(this.canvas, topLeftOffset, scale);
        }
        else {
            L.DomUtil.setPosition(this.canvas, topLeftOffset);
        }
    }
    setMouseRange(range) {
        this.mouse_range = range;
    }
    handleClick(_e) { }
    addLayerTo(map) {
        map.addLayer(this);
        return this;
    }
    addTo(map) {
        this.addLayerTo(map);
        return this;
    }
    canvasReset() {
        let map = this.getMap();
        this._center = map.getCenter();
        this._zoom = map.getZoom();
        let canvas = this.getCanvas();
        let size = map.getSize();
        canvas.width = size.x;
        canvas.height = size.y;
        this._updateTransform(this._center, this._zoom);
        this.drawCanvas();
    }
    transform_xy([lon, lat]) {
        return this.transform_latlon([lat, lon]);
    }
    transform_latlon(point) {
        const map = this.getMap();
        //transform the point to layer coordinates and then
        //correct for the layer offset
        return map
            .latLngToLayerPoint(point)
            .add(map.layerPointToContainerPoint([0, 0]));
    }
    redraw() {
        if (this.moving) {
            this.canvasReset();
        }
        else {
            this.drawCanvas();
        }
    }
    render_linestring(ctx, linestring) {
        if (linestring.coordinates.length == 0) {
            return;
        }
        if (linestring.type === "MultiLineString") {
            linestring.coordinates.forEach((p) => this._render_linestring(ctx, p));
        }
        else {
            this._render_linestring(ctx, linestring.coordinates);
        }
    }
    _render_linestring(ctx, coords) {
        if (coords.length == 0) {
            return;
        }
        let start = this.transform_xy(coords[0]);
        ctx.moveTo(start.x, start.y);
        coords.slice(1).forEach((coord) => {
            let point = this.transform_xy(coord);
            ctx.lineTo(point.x, point.y);
        });
    }
    render_polygon(ctx, polygon) {
        if (polygon.coordinates.length == 0) {
            return;
        }
        if (polygon.type === "MultiPolygon") {
            polygon.coordinates.forEach((p) => this._render_polygon(ctx, p));
        }
        else {
            this._render_polygon(ctx, polygon.coordinates);
        }
    }
    _render_polygon(ctx, polygon) {
        this._render_linestring(ctx, polygon[0]);
        let holes = polygon.slice(1);
        if (holes && holes.length) {
            holes.forEach((hole) => {
                this._render_linestring(ctx, hole);
            });
        }
    }
}
export class StorageCanvasLayer extends CanvasLayer {
    constructor(storage) {
        super();
        this.options = {
            filterPointsInBounds: true,
            zIndex: 400,
            padding: 0,
        };
        this._selectedRecords = new BehaviorSubject([]);
        this.selectedRecords = this._selectedRecords
            .asObservable()
            .pipe(filter((e) => e.length > 0));
        this.data = storage;
    }
    setData(data) {
        this.data.setData(data);
    }
    addData(data) {
        this.data.addData(data);
        this.drawCanvas();
    }
    _getPoints(bounds) {
        let map = this.getMap();
        if (!bounds) {
            if (!this.options.filterPointsInBounds) {
                bounds = new L.LatLngBounds([-90, -180], [90, 180]);
            }
            else {
                bounds = map.getBounds();
            }
        }
        return this.data.getData(bounds);
    }
    handleClick(e) {
        let map = this.getMap();
        var j = e.containerPoint;
        var bounds = L.latLngBounds(map.containerPointToLatLng(j.add(L.point(this.mouse_range, this.mouse_range))), map.containerPointToLatLng(j.subtract(L.point(this.mouse_range, this.mouse_range))));
        var items = this._getPoints(bounds);
        if (items && items.length > 0) {
            this._selectedRecords.next(items);
        }
    }
    drawCanvas() {
        const canvas = this.getCanvas();
        const ctx = canvas.getContext("2d");
        if (!ctx) {
            throw new Error("Unable to aquire context, please check map and canvas");
        }
        // clear canvas
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        let points = this._getPoints();
        points.forEach((item) => {
            this.renderItem(item);
        });
    }
    renderItem(item) {
        const ctx = this.getCanvas().getContext("2d");
        if (!ctx) {
            return;
        }
        ctx.beginPath();
        ctx.arc(item.x, item.y, 2, 0, 2 * Math.PI, true);
        ctx.fillStyle = "rgba(255,0,0, 0.5)";
        ctx.fill();
    }
}
export class QuadTreeCanvasLayer extends StorageCanvasLayer {
    constructor() {
        super(new QuadTreeStorage());
    }
}
export class ListCanvasLayer extends StorageCanvasLayer {
    constructor() {
        super(new ListStorage());
    }
}
